import { addressHtml, businessIdsHtml, escapeHtml as esc, invoiceNumberOf, printHtml, type DocumentSalon } from '../../utils/invoice'
import { printPackingSlip } from '../inbox/packingSlip'
import { brandColors } from '../../data/brandColors'
import { exVat, refundVatByRate } from '../../core/rules/revenue'
import { orderNumber, vatBreakdown } from './orderLabels'
import { money } from '../../core/format/currency'
import type { AdminOrder, InboxOrder, OrderRefund } from '../../core/types/adminRequests'
import type { Product } from '../../core/types/catalog'

type Salon = DocumentSalon

/**
 * De factuur van een bestelling, met de btw per regel zoals die bij de verkoop is bewaard
 * (nooit het tarief van nu). Terugbetalingen staan eronder als creditregels.
 * Met de echte server wordt dit een PDF van de server (LARAVEL.md §9.6).
 */
export function orderInvoiceHtml(o: AdminOrder, salon: Salon, dateLabel: string): string {
  const a = o.address
  const vat = vatBreakdown(o)
  const number = invoiceNumberOf(salon, orderNumber(o))
  const rows = o.lines
    .map(
      (l) =>
        `<tr><td>${esc(l.name)}</td><td class="r">${l.qty}</td><td class="r">${l.vatRate}%</td><td class="r">${esc(money(exVat(l.lineTotalCents, l.vatRate)))}</td><td class="r">${esc(money(l.lineTotalCents))}</td></tr>`,
    )
    .join('')
  const refunds = o.refunds
    .map(
      (r) =>
        `<tr><td class="muted">Terugbetaald ${esc(r.at.slice(0, 10))} · ${esc(r.reason)}${r.voucherRestoredCents ? ` · ${esc(money(r.voucherRestoredCents))} terug op cadeaubon` : ''}</td><td class="r">−${esc(money(r.amountCents))}</td></tr>`,
    )
    .join('')

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>Factuur ${esc(number)}</title>
<style>
@page{margin:16mm}
body{font-family:Inter,system-ui,sans-serif;color:${brandColors.ink};margin:48px;font-size:14px;line-height:1.5}
@media print{body{margin:0}}
h1{font-family:Fraunces,Georgia,serif;font-weight:300;font-size:32px;margin:0 0 4px}
.muted{color:${brandColors.muted}}.row{display:flex;justify-content:space-between;gap:24px}
table{width:100%;border-collapse:collapse;margin-top:32px}th,td{text-align:left;padding:10px 0;border-bottom:1px solid ${brandColors.line}}
th{font-weight:400;color:${brandColors.muted}}td.r,th.r{text-align:right}.tot td{border:0;padding:4px 0}.big td{font-size:16px;padding-top:12px}
</style></head><body>
<div class="row"><div><h1>Factuur</h1><div class="muted">${esc(number)} · ${esc(dateLabel)}</div></div>
<div style="text-align:right"><strong>${esc(salon.name)}</strong><br>${esc(salon.address)}<br>${esc(salon.email)}<br>${businessIdsHtml(salon, '<br>')}</div></div>
<p style="margin-top:32px">${esc(`${a.firstName} ${a.lastName}`.trim() || o.customerName)}<br>${addressHtml(a)}</p>
<table><thead><tr><th>Product</th><th class="r">Aantal</th><th class="r">Btw</th><th class="r">Excl. btw</th><th class="r">Bedrag</th></tr></thead><tbody>
${rows}
</tbody></table>
<table><tbody class="tot">
<tr><td class="muted">Verzending (btw ${o.shippingVatRate}%)</td><td class="r">${o.shippingCents ? esc(money(o.shippingCents)) : 'Gratis'}</td></tr>
${vat.map((v) => `<tr><td class="muted">Waarvan btw ${v.vatRate}%</td><td class="r">${esc(money(v.vatCents))}</td></tr>`).join('')}
<tr class="big"><td>Totaal</td><td class="r">${esc(money(o.totalCents))}</td></tr>
${refunds}
</tbody></table>
<p class="muted" style="margin-top:48px">Betaald. Dank je wel voor je bestelling.</p>
</body></html>`
}

export const printOrderInvoice = (o: AdminOrder, salon: Salon, dateLabel: string) =>
  printHtml(orderInvoiceHtml(o, salon, dateLabel), `factuur-${invoiceNumberOf(salon, o.number ?? o.id)}.html`, 'Factuur')

/** Het nummer van een creditnota: van de server, anders bestelnummer-C1, -C2, … (volgorde van terugbetalen). */
export function creditNumber(o: AdminOrder, r: OrderRefund): string {
  if (r.creditNumber) return r.creditNumber
  const i = [...o.refunds].sort((a, b) => a.at.localeCompare(b.at)).findIndex((x) => x.id === r.id)
  return `${o.number ?? o.id}-C${i + 1}`
}

/**
 * De creditnota van één terugbetaling: een eigen document met een eigen nummer en datum, dat
 * naar de oorspronkelijke factuur verwijst. De factuur zelf verandert nooit (LARAVEL.md §9.6).
 * Btw per tarief met dezelfde verdeling als het grootboek, dus gelijk aan Omzet en de export.
 */
export function creditNoteHtml(o: AdminOrder, r: OrderRefund, salon: Salon): string {
  const a = o.address
  const number = creditNumber(o, r)
  const vat = refundVatByRate(o, r)
  const returned = (r.lines ?? [])
    .map((l) => {
      const line = o.lines.find((x) => x.productId === l.productId)
      return `<tr><td>${esc(line?.name ?? l.productId)}</td><td class="r">${l.qty}</td><td class="r">${line ? `${line.vatRate}%` : ''}</td></tr>`
    })
    .join('')

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>Creditnota ${esc(number)}</title>
<style>
@page{margin:16mm}
body{font-family:Inter,system-ui,sans-serif;color:${brandColors.ink};margin:48px;font-size:14px;line-height:1.5}
@media print{body{margin:0}}
h1{font-family:Fraunces,Georgia,serif;font-weight:300;font-size:32px;margin:0 0 4px}
.muted{color:${brandColors.muted}}.row{display:flex;justify-content:space-between;gap:24px}
table{width:100%;border-collapse:collapse;margin-top:32px}th,td{text-align:left;padding:10px 0;border-bottom:1px solid ${brandColors.line}}
th{font-weight:400;color:${brandColors.muted}}td.r,th.r{text-align:right}.tot td{border:0;padding:4px 0}.big td{font-size:16px;padding-top:12px}
</style></head><body>
<div class="row"><div><h1>Creditnota</h1><div class="muted">${esc(number)} · ${esc(r.at.slice(0, 10))}</div><div class="muted">Bij factuur ${esc(invoiceNumberOf(salon, orderNumber(o)))}</div></div>
<div style="text-align:right"><strong>${esc(salon.name)}</strong><br>${esc(salon.address)}<br>${esc(salon.email)}<br>${businessIdsHtml(salon, '<br>')}</div></div>
<p style="margin-top:32px">${esc(`${a.firstName} ${a.lastName}`.trim() || o.customerName)}<br>${addressHtml(a)}</p>
<p>Reden: ${esc(r.reason)}</p>
${returned ? `<table><thead><tr><th>Teruggenomen</th><th class="r">Aantal</th><th class="r">Btw</th></tr></thead><tbody>${returned}</tbody></table>` : ''}
<table><tbody class="tot">
${vat.map((v) => `<tr><td class="muted">Excl. btw ${v.vatRate}%</td><td class="r">−${esc(money(v.grossCents - v.vatCents))}</td></tr><tr><td class="muted">Btw ${v.vatRate}%</td><td class="r">−${esc(money(v.vatCents))}</td></tr>`).join('')}
<tr class="big"><td>Totaal terug</td><td class="r">−${esc(money(r.amountCents))}</td></tr>
${r.voucherRestoredCents ? `<tr><td class="muted">Daarnaast terug op je cadeaubon</td><td class="r">${esc(money(r.voucherRestoredCents))}</td></tr>` : ''}
</tbody></table>
<p class="muted" style="margin-top:48px">Het bedrag is teruggestort via de betaalmethode van je bestelling.</p>
</body></html>`
}

export const printCreditNote = (o: AdminOrder, r: OrderRefund, salon: Salon) =>
  printHtml(creditNoteHtml(o, r, salon), `creditnota-${creditNumber(o, r)}.html`, 'Creditnota')

/** De pakbon van de inbox, vanuit een bestelling in het overzicht. */
export function printOrderPackingSlip(o: AdminOrder, productById: (id: string) => Product | undefined, salon: Pick<Salon, 'name' | 'address' | 'email'>) {
  const asInbox: InboxOrder = {
    id: o.id,
    receivedAt: o.placedAt,
    customerId: o.customerId,
    name: o.customerName,
    city: o.address.city,
    status: o.fulfilment === 'bezorgd' ? 'verzonden' : o.fulfilment,
    payment: { status: o.paymentStatus, method: o.paymentMethod },
    totalCents: o.totalCents,
    gift: o.gift,
    order: {
      email: o.email,
      address: o.address,
      lines: o.lines.map((l) => ({ productId: l.productId, qty: l.qty, subscribe: o.channel === 'abonnement' })),
      code: o.code,
      skinNote: o.skinNote,
      refillReminder: false,
    },
  }
  return printPackingSlip(asInbox, productById, salon, o.gift)
}
